import * as React from "react";
import { UploadCloud, Loader2, CheckCircle2, Pencil, Trash2, Move, ZoomIn, Crop } from "lucide-react";
import { toast } from "sonner";

interface SupabaseUploadProps {
  onUploadSuccess: (url: string) => void;
  onRemove?: () => void;
  label?: string;
  currentUrl?: string | null;
  accept?: string;
  className?: string;
  cropShape?: "circle" | "rounded" | "rect";
  aspectRatio?: number;
  disableCrop?: boolean;
  mode?: "dropzone" | "button" | "custom";
  children?: React.ReactNode | ((trigger: () => void, uploading: boolean, progress: number) => React.ReactNode);
}

export function SupabaseUpload({
  onUploadSuccess,
  onRemove,
  label = "Arraste um arquivo ou clique para fazer upload",
  currentUrl,
  accept = "image/*",
  className = "",
  cropShape = "circle",
  aspectRatio = 1,
  disableCrop = false,
  mode = "dropzone",
  children,
}: SupabaseUploadProps) {
  const [uploading, setUploading] = React.useState(false);
  const [progress, setProgress] = React.useState(0);
  const [dragActive, setDragActive] = React.useState(false);
  const [showCropModal, setShowCropModal] = React.useState(false);
  const [tempFileUrl, setTempFileUrl] = React.useState<string | null>(null);

  const fileInputRef = React.useRef<HTMLInputElement>(null);

  // States for Professional Interactive Canvas Cropper
  const [zoom, setZoom] = React.useState(1);
  const [offsetX, setOffsetX] = React.useState(0);
  const [offsetY, setOffsetY] = React.useState(0);
  const [isDraggingCanvas, setIsDraggingCanvas] = React.useState(false);
  const [dragStart, setDragStart] = React.useState({ x: 0, y: 0 });

  const handleUpload = async (file: File) => {
    setUploading(true);
    setProgress(15);

    const progressInterval = setInterval(() => {
      setProgress((prev) => {
        if (prev >= 90) {
          clearInterval(progressInterval);
          return 90;
        }
        return prev + 15;
      });
    }, 150);

    const formData = new FormData();
    formData.append("file", file);

    try {
      const res = await fetch("/api/upload", {
        method: "POST",
        body: formData,
      });

      const data = await res.json();
      if (!res.ok || data.error) {
        throw new Error(data.error || "Erro ao realizar o upload do arquivo");
      }

      setProgress(100);
      setTimeout(() => {
        onUploadSuccess(data.url);
        setShowCropModal(false);
        setTempFileUrl(null);
        setUploading(false);
        setProgress(0);
        toast.success("Upload concluído com sucesso!");
      }, 300);
    } catch (err: any) {
      clearInterval(progressInterval);
      console.error(err);
      toast.error("Erro no upload", {
        description: err.message || "Não foi possível enviar o arquivo.",
      });
      setUploading(false);
      setProgress(0);
    }
  };

  const handleSelectFile = (file: File) => {
    const isAudio =
      accept.includes("audio") ||
      accept.includes(".mp3") ||
      accept.includes(".wav") ||
      file.type.startsWith("audio/") ||
      file.name.toLowerCase().endsWith(".mp3") ||
      file.name.toLowerCase().endsWith(".wav");

    if (disableCrop || isAudio) {
      handleUpload(file);
    } else {
      const url = URL.createObjectURL(file);
      setTempFileUrl(url);
      setZoom(1);
      setOffsetX(0);
      setOffsetY(0);
      setShowCropModal(true);
    }
  };

  const handleDrag = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === "dragenter" || e.type === "dragover") {
      setDragActive(true);
    } else if (e.type === "dragleave") {
      setDragActive(false);
    }
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);

    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      handleSelectFile(e.dataTransfer.files[0]);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    e.preventDefault();
    if (e.target.files && e.target.files[0]) {
      handleSelectFile(e.target.files[0]);
    }
  };

  const triggerInput = () => {
    fileInputRef.current?.click();
  };

  return (
    <div className={`font-sans ${className}`}>
      <input
        ref={fileInputRef}
        type="file"
        accept={accept}
        className="hidden"
        onChange={handleChange}
        disabled={uploading}
      />

      {mode === "custom" ? (
        typeof children === "function" ? (
          children(triggerInput, uploading, progress)
        ) : (
          <div onClick={uploading ? undefined : triggerInput} className="cursor-pointer">
            {children}
          </div>
        )
      ) : mode === "button" ? (
        <button
          type="button"
          onClick={uploading ? undefined : triggerInput}
          disabled={uploading}
          className={`relative overflow-hidden inline-flex items-center justify-center gap-2 px-4 py-2 rounded-xl text-xs font-bold transition-all cursor-pointer shadow-sm ${uploading
            ? "bg-blue-100 text-blue-700 dark:bg-blue-950 dark:text-blue-300 cursor-wait"
            : "bg-[#004EB1] hover:bg-[#003d8d] active:scale-95 text-white"
            } ${className}`}
        >
          {uploading ? (
            <>
              <Loader2 className="size-3.5 animate-spin" />
              <span>Enviando ({progress}%)</span>
              <div
                className="absolute bottom-0 left-0 h-1 bg-emerald-500 transition-all duration-200"
                style={{ width: `${progress}%` }}
              />
            </>
          ) : (
            <>
              <UploadCloud className="size-4" />
              <span>{label}</span>
            </>
          )}
        </button>
      ) : currentUrl ? (
        /* VISUALIZAÇÃO DA IMAGEM ATUAL COM BOTÕES DE AÇÃO */
        <div className="relative group border border-zinc-200 dark:border-zinc-800 bg-white dark:bg-zinc-950 rounded-xl p-3 flex items-center justify-between gap-4 shadow-sm hover:border-[#004EB1]/40 transition-all">
          <div className="flex items-center gap-3 min-w-0">
            <div className={`size-14 bg-zinc-100 dark:bg-zinc-900 border border-zinc-200 dark:border-zinc-800 overflow-hidden shrink-0 flex items-center justify-center ${cropShape === "circle" ? "rounded-full" : cropShape === "rounded" ? "rounded-xl" : "rounded-lg"
              }`}>
              {currentUrl.startsWith("emoji:") ? (
                <span className="text-2xl">{currentUrl.split(":")[1]}</span>
              ) : (
                <img src={currentUrl} alt="Anexo Carregado" className="w-full h-full object-cover" />
              )}
            </div>
            <div className="min-w-0">
              <p className="text-xs font-bold text-zinc-900 dark:text-white truncate">Anexo</p>
              <p className="text-[10px] text-zinc-400 truncate">Clique em alterar para selecionar nova imagem.</p>
            </div>
          </div>

          <div className="flex items-center gap-2 shrink-0">
            <button
              type="button"
              onClick={triggerInput}
              className="bg-zinc-100 hover:bg-zinc-200 dark:bg-zinc-850 dark:hover:bg-zinc-800 text-zinc-700 dark:text-zinc-200 text-xs font-bold px-3 py-1.5 rounded-lg flex items-center gap-1.5 transition-colors cursor-pointer"
              title="Alterar"
            >
              <Pencil className="size-3.5 text-[#004EB1]" />
            </button>
            {onRemove && (
              <button
                type="button"
                onClick={onRemove}
                className="bg-red-50 hover:bg-red-100 dark:bg-red-955/30 dark:hover:bg-red-900/40 text-red-600 dark:text-red-400 text-xs font-bold px-2.5 py-1.5 rounded-lg flex items-center gap-1.5 transition-colors cursor-pointer"
                title="Remover"
              >
                <Trash2 className="size-3.5" />
              </button>
            )}
          </div>
        </div>
      ) : (
        /* ÁREA DE DROPZONE SHADCN ATTACHMENT QUANDO NÃO HÁ IMAGEM */
        <div
          onDragEnter={handleDrag}
          onDragOver={handleDrag}
          onDragLeave={handleDrag}
          onDrop={handleDrop}
          onClick={uploading ? undefined : triggerInput}
          className={`relative border-2 border-dashed rounded-xl p-5 flex flex-col items-center justify-center gap-2 transition-all duration-200 text-center select-none ${uploading
            ? "border-blue-300 bg-blue-50/5 dark:bg-blue-950/5 animate-pulse cursor-wait"
            : dragActive
              ? "border-[#004EB1] bg-zinc-50 dark:bg-zinc-900 cursor-copy scale-[0.99]"
              : "border-zinc-200 dark:border-zinc-800 hover:border-zinc-300 dark:hover:border-zinc-700 cursor-pointer"
            }`}
        >
          {uploading ? (
            <>
              <div className="size-9 rounded-full bg-blue-50 dark:bg-blue-950/50 flex items-center justify-center">
                <Loader2 className="size-4 text-blue-500 animate-spin" />
              </div>
              <p className="text-xs font-semibold text-zinc-800 dark:text-zinc-200">Enviando e processando arquivo...</p>
            </>
          ) : (
            <>
              <div className={`size-9 rounded-full flex items-center justify-center ${dragActive ? "bg-blue-50 dark:bg-blue-950/30 text-[#004EB1]" : "bg-zinc-50 dark:bg-zinc-900 text-zinc-400"
                }`}>
                <UploadCloud className="size-5" />
              </div>
              <div className="space-y-0.5">
                <p className="text-xs font-semibold text-zinc-700 dark:text-zinc-200">
                  {dragActive ? "Solte o arquivo aqui!" : label}
                </p>
                <p className="text-[10px] text-zinc-400">Suporta arquivos de até 10MB</p>
              </div>
            </>
          )}
        </div>
      )}

      {/* MODAL PROFISSIONAL DE CROP E REDIMENSIONAMENTO INTERATIVO */}
      {showCropModal && tempFileUrl && (
        <div className="fixed inset-0 bg-black/75 backdrop-blur-md flex items-center justify-center z-[9999] animate-in fade-in duration-200 p-4 font-sans">
          <div className="bg-white dark:bg-zinc-900 border border-zinc-200 dark:border-zinc-800 rounded-2xl w-full max-w-lg overflow-hidden shadow-2xl animate-in zoom-in-95 duration-200">
            <div className="p-4 border-b border-zinc-150 dark:border-zinc-800 flex items-center justify-between">
              <h3 className="text-sm font-bold text-zinc-900 dark:text-white flex items-center gap-2">
                <Crop className="size-4 text-[#004EB1]" />
                Ajuste e Enquadramento da Imagem
              </h3>
              <button
                type="button"
                onClick={() => { setShowCropModal(false); setTempFileUrl(null); }}
                className="text-zinc-400 hover:text-zinc-600 dark:hover:text-zinc-200 text-sm font-bold cursor-pointer"
              >
                ✕
              </button>
            </div>

            <div className="p-6 space-y-5">
              {/* Canvas de Visualização Interativo */}
              {(() => {
                const targetAspect = aspectRatio || (cropShape === "rect" ? 3 : 1);
                const isRect = cropShape === "rect";
                const previewWidth = isRect ? 420 : 240;
                const previewHeight = isRect ? Math.max(120, Math.round(420 / targetAspect)) : 240;

                return (
                  <div className="flex justify-center">
                    <div
                      onMouseDown={(e) => {
                        setIsDraggingCanvas(true);
                        setDragStart({ x: e.clientX - offsetX, y: e.clientY - offsetY });
                      }}
                      onMouseMove={(e) => {
                        if (isDraggingCanvas) {
                          setOffsetX(e.clientX - dragStart.x);
                          setOffsetY(e.clientY - dragStart.y);
                        }
                      }}
                      onMouseUp={() => setIsDraggingCanvas(false)}
                      onMouseLeave={() => setIsDraggingCanvas(false)}
                      style={{ width: previewWidth, height: previewHeight }}
                      className={`relative overflow-hidden border-2 border-[#004EB1] bg-black shadow-inner cursor-grab active:cursor-grabbing select-none ${cropShape === "circle" ? "rounded-full" : cropShape === "rounded" ? "rounded-3xl" : "rounded-xl"
                        }`}
                    >
                      <img
                        src={tempFileUrl}
                        alt="Target Crop"
                        draggable={false}
                        className="w-full h-full object-cover transition-transform duration-75"
                        style={{
                          transform: `scale(${zoom}) translate(${offsetX}px, ${offsetY}px)`,
                        }}
                      />
                      <div className="absolute inset-0 pointer-events-none border border-white/20 rounded-inherit" />
                    </div>
                  </div>
                );
              })()}

              {/* Controles de Zoom e Posição */}
              <div className="space-y-3 bg-zinc-50 dark:bg-zinc-950 p-4 rounded-xl border border-zinc-150 dark:border-zinc-800 text-xs">
                <div className="flex items-center justify-between gap-4">
                  <span className="font-semibold text-zinc-700 dark:text-zinc-300 flex items-center gap-1.5">
                    <ZoomIn className="size-4 text-[#004EB1]" />
                    Zoom ({zoom.toFixed(2)}x)
                  </span>
                  <input
                    type="range"
                    min="1"
                    max="3"
                    step="0.05"
                    value={zoom}
                    onChange={(e) => setZoom(parseFloat(e.target.value))}
                    className="w-44 accent-[#004EB1] h-1.5 bg-zinc-200 dark:bg-zinc-800 rounded-lg cursor-pointer"
                  />
                </div>
                <div className="flex items-center justify-between text-[11px] text-zinc-400">
                  <span className="flex items-center gap-1">
                    <Move className="size-3.5" />
                    Arraste a imagem no quadrado para mover a posição
                  </span>
                  <button
                    type="button"
                    onClick={() => { setZoom(1); setOffsetX(0); setOffsetY(0); }}
                    className="text-[#004EB1] dark:text-blue-400 font-bold hover:underline cursor-pointer"
                  >
                    Resetar
                  </button>
                </div>
              </div>

              {/* Botões do Modal */}
              <div className="flex justify-end gap-2 pt-2 border-t border-zinc-150 dark:border-zinc-800">
                <button
                  type="button"
                  onClick={() => { setShowCropModal(false); setTempFileUrl(null); }}
                  className="px-4 py-2 text-xs font-semibold text-zinc-600 dark:text-zinc-300 hover:bg-zinc-100 dark:hover:bg-zinc-800 rounded-lg cursor-pointer"
                >
                  Cancelar
                </button>
                <button
                  type="button"
                  onClick={async () => {
                    try {
                      setUploading(true);
                      const img = new Image();
                      img.crossOrigin = "anonymous";
                      img.src = tempFileUrl;
                      await new Promise((res, rej) => {
                        img.onload = res;
                        img.onerror = rej;
                      });

                      const targetAspect = aspectRatio || (cropShape === "rect" ? 3 : 1);
                      const isRect = cropShape === "rect";
                      const previewWidth = isRect ? 420 : 240;
                      const previewHeight = isRect ? Math.max(120, Math.round(420 / targetAspect)) : 240;

                      const canvas = document.createElement("canvas");
                      if (cropShape === "rect") {
                        canvas.width = 1920;
                        canvas.height = Math.round(1920 / targetAspect);
                      } else {
                        const size = 600;
                        canvas.width = size;
                        canvas.height = cropShape === "circle" ? size : Math.round(size / targetAspect);
                      }
                      const ctx = canvas.getContext("2d");

                      if (ctx) {
                        ctx.imageSmoothingEnabled = true;
                        ctx.imageSmoothingQuality = "high";

                        if (cropShape === "circle") {
                          ctx.beginPath();
                          ctx.arc(canvas.width / 2, canvas.height / 2, canvas.width / 2, 0, Math.PI * 2);
                          ctx.clip();
                        } else if (cropShape === "rounded") {
                          ctx.beginPath();
                          ctx.roundRect(0, 0, canvas.width, canvas.height, 80);
                          ctx.clip();
                        }

                        const scaleFactorX = canvas.width / previewWidth;
                        const scaleFactorY = canvas.height / previewHeight;

                        const cx = canvas.width / 2;
                        const cy = canvas.height / 2;

                        ctx.save();
                        ctx.translate(cx + offsetX * scaleFactorX, cy + offsetY * scaleFactorY);
                        ctx.scale(zoom, zoom);

                        const scale = Math.max(canvas.width / img.width, canvas.height / img.height);
                        const dw = img.width * scale;
                        const dh = img.height * scale;

                        ctx.drawImage(img, -dw / 2, -dh / 2, dw, dh);
                        ctx.restore();

                        canvas.toBlob(async (blob) => {
                          if (blob) {
                            const croppedFile = new File([blob], "cropped-image.png", { type: "image/png" });
                            await handleUpload(croppedFile);
                          }
                        }, "image/png");
                      }
                    } catch (e) {
                      toast.error("Erro ao aplicar crop na imagem.");
                      setUploading(false);
                    }
                  }}
                  className="bg-[#004EB1] hover:bg-[#003d8d] text-white text-xs font-semibold px-5 py-2 rounded-lg cursor-pointer shadow-md"
                >
                  Salvar Imagem Cropada
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
